import React, { useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useLanguage } from "../components/LanguageContext";

const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  card: "#ffffff",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  shadow: "0 10px 30px rgba(0,0,0,.08)",
};

export default function PaymentSuccess() {
  const { t, language } = useLanguage();

  useEffect(() => {
    document.title = t("titles.paymentsuccess");
  }, [language, t]);

  useEffect(() => {
    const t = setTimeout(() => {
      window.location.href = "/";
    }, 6000);
    return () => clearTimeout(t);
  }, []);

  const T = (ka, en) => (language === "ka" ? ka : en);

  return (
    <Wrap>
      <Card>
        <IconWrap aria-hidden>
          <Burst />
          <Check>✅</Check>
        </IconWrap>

        <Title>
          {T(
            "მადლობა, გადახდა წარმატებით შესრულდა!",
            "Thank you, your payment was successful!"
          )}
        </Title>

        <P>
          {T(
            "მალე გადამისამართდებით მთავარ გვერდზე...",
            "You will be redirected to the home page shortly..."
          )}
        </P>
      </Card>
    </Wrap>
  );
}

/* ======= Styles ======= */

const Wrap = styled.div`
  /* სრულყოფილი ცენტრი და ჰორიზონტალური გადაცდენის პრევენცია */
  width: 100%;
  display: grid;
  place-items: center;
  box-sizing: border-box;
  padding-inline: clamp(12px, 4vw, 24px);
  padding-block: clamp(20px, 5vh, 36px);
  overflow-x: hidden;

  background:
    radial-gradient(1200px 500px at 80% -20%, rgba(161, 90, 90, 0.06), transparent 60%),
    radial-gradient(900px 400px at -10% 110%, rgba(17, 24, 39, 0.05), transparent 55%),
    ${COLORS.subtle};
`;

const Card = styled.div`
  width: 100%;
  max-width: 520px;

  /* თანაბარი გვერდითი შუში ყველა ზომაზე */
  margin: 0 clamp(14px, 4vw, 24px);

  /* რომ არაფერმა არ „გადაიქაჩოს“ */
  box-sizing: border-box;

  background: ${COLORS.card};
  border: 1px solid ${COLORS.line};
  border-radius: 18px;
  box-shadow: ${COLORS.shadow};

  padding: clamp(20px, 4vw, 28px) clamp(16px, 4vw, 24px);
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    border-top-left-radius: 18px;
    border-top-right-radius: 18px;
    background: linear-gradient(90deg, ${COLORS.brand}, ${COLORS.brandDark});
  }
`;

const pop = keyframes`
  0%   { transform: scale(.85); opacity: .6; }
  60%  { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); }
`;

const pulse = keyframes`
  0%   { transform: scale(1);   opacity: .45; }
  70%  { transform: scale(1.25); opacity: 0; }
  100% { transform: scale(1.25); opacity: 0; }
`;

const IconWrap = styled.div`
  width: clamp(64px, 10vw, 76px);
  height: clamp(64px, 10vw, 76px);
  margin: 4px auto 14px;
  border-radius: 999px;
  background: #10b98122; /* soft emerald */
  border: 1px solid ${COLORS.line};
  display: grid;
  place-items: center;
  position: relative;
  animation: ${pop} .38s ease-out both;
`;

const Burst = styled.span`
  position: absolute;
  inset: 0;
  border-radius: 999px;
  background: #10b98133; /* faint emerald halo */
  animation: ${pulse} 1.6s ease-out 0.12s both;
`;

const Check = styled.span`
  font-size: clamp(28px, 6vw, 34px);
  line-height: 1;
`;

const Title = styled.h1`
  margin: 6px 0 6px;
  font-size: clamp(18px, 4.6vw, 20px);
  font-weight: 900;
  color: ${COLORS.text};
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const P = styled.p`
  margin: 6px 0 0;
  color: ${COLORS.dim};
  font-size: clamp(13px, 3.8vw, 14px);
  line-height: 1.6;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;
